/**
 * 分页契约 —— **分页口径只在一处实现**（架构 §6.4「统一分页中间件」）
 *
 * 依据：架构 **§6.4** · `DR-006`（页 50 / 默认近 3 月）· C 域 §5.1.3（分页单元 = **交易**）。
 *
 * ## 🔴 一条纪律
 * **禁止各接口自定义分页参数或默认值**（架构 §6.4 原文）。
 * ⇒ 解析、上限裁剪、默认时间窗、`hasMore` 判定**全部在本文件**；
 * 服务端中间件只负责"调一次并挂到请求上"，各 controller **只读不解析**。
 *
 * ## 两处刻意的设计
 *
 * 1. **不返回 `totalPages`** ——「共 N 页」需要 `ceil(total / pageSize)`，而**全项目禁除法**
 *    （§22.1 · `AC-P1.2`，白名单只覆盖金额定点函数）。前端要「还有更多」只需 `hasMore`
 *    （`offset + 本页条数 < total`，纯加法与比较）。**少一个字段，少一处破规则的理由。**
 * 2. **时间窗不在中间件里定** —— 架构 §6.4 把"时间窗默认值"与分页参数并列写在同一层。
 *    🔴 **实测落地时该处必须拆开**：NestJS 中**中间件先于守卫执行**，此刻 `X-Ledger-Id`
 *    尚未被解析、**账本时区未知** ⇒ 中间件里**算不出**"近 3 个月的起止"。
 *    ⇒ 中间件只做**与租户无关**的部分（`page` / `pageSize` / 裁剪），
 *    时间窗由 {@link resolvePageWindow} 在**拿到 `today` 之后**（守卫之后）解析。
 *    **口径仍只此一份实现**，只是执行时机后移了一格。
 */

import { z } from 'zod';
import { ListConfig } from '../constants/index.js';
import { recentMonthsStart } from '../nucleus/calendar.js';

/** `YYYY-MM-DD`（账本本地日历日）—— 日历事实，**无时区**（架构 §4.5） */
export const DayString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, '须为 YYYY-MM-DD（账本本地日历日）');

/**
 * 分页入参契约。
 *
 * ⚠️ `z.coerce` 是必要的：query string 里 `?page=2` 是字符串。
 * `pageSize` 的 `.max()` 即**上限裁剪**（架构 §6.4 原文），超上限**直接 400**
 * —— 不静默截断，否则客户端会以为拿到了自己请求的页长。
 */
export const PageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(ListConfig.MaxPageSize).default(ListConfig.PageSize),
  /** 缺省 = 近 3 个自然月（`DR-006`），由 {@link resolvePageWindow} 以账本本地日补 */
  from: DayString.optional(),
  /** 缺省 = 今天（账本本地日） */
  to: DayString.optional(),
});

export type PageQueryInput = z.input<typeof PageQuerySchema>;

/** 解析后的分页参数（`offset` 已算好，调用方直接用） */
export interface PageParams {
  page: number;
  pageSize: number;
  /** `(page − 1) × pageSize` */
  offset: number;
  /** 用户显式指定时才存在（`undefined` = 走默认近 3 月） */
  from?: string | undefined;
  to?: string | undefined;
}

/** 分页键（中间件据此从 query 里**摘取**，其余键原样留给筛选逻辑） */
export const PAGE_KEYS = ['page', 'pageSize', 'from', 'to'] as const;

/**
 * 解析分页参数。
 *
 * 🔴 **只吃这 4 个键**（其余筛选键不解析、不校验 —— 各域筛选取值面不同）。
 * 解析失败即抛（由 `AllExceptionsFilter` 转成 `VALIDATION_FAILED` 400）。
 */
export function parsePageQuery(raw: Record<string, unknown>): PageParams {
  const picked: Record<string, unknown> = {};
  for (const k of PAGE_KEYS) {
    if (raw[k] !== undefined) picked[k] = raw[k];
  }
  const r = PageQuerySchema.safeParse(picked);
  if (!r.success) {
    throw new RangeError(`分页参数非法：${r.error.issues.map((i) => `${i.path.join('.')} ${i.message}`).join('；')}`);
  }
  const { page, pageSize, from, to } = r.data;
  return {
    page,
    pageSize,
    offset: (page - 1) * pageSize,
    ...(from === undefined ? {} : { from }),
    ...(to === undefined ? {} : { to }),
  };
}

/** 时间窗（**半开区间 `[from, to]` 闭区间语义**：两边都含 —— 日历日比较是闭的） */
export interface PageWindow {
  from: string;
  to: string;
}

/**
 * 补默认时间窗并做一致性校验。
 *
 * @param today 账本本地日历日（🔴 由**守卫之后**的账本上下文提供，不能用服务器时区自己算）
 */
export function resolvePageWindow(
  params: PageParams,
  today: string,
  defaultMonths: number = ListConfig.DefaultWindowMonths,
): PageWindow {
  const from = params.from ?? recentMonthsStart(today, defaultMonths);
  const to = params.to ?? today;
  if (from > to) {
    throw new RangeError(`时间窗起点不得晚于终点：from=${from} to=${to}`);
  }
  return { from, to };
}

/** 分页响应体（**接口只回口径结果**：`items` 已按页切好，`total` 供"还有更多"判断） */
export interface Paged<T> {
  items: readonly T[];
  page: number;
  pageSize: number;
  total: number;
  /** `offset + 本页条数 < total` —— **纯加法，无除法**（见文件头设计说明 1） */
  hasMore: boolean;
}

/** 组装分页响应 */
export function paged<T>(items: readonly T[], params: PageParams, total: number): Paged<T> {
  return {
    items,
    page: params.page,
    pageSize: params.pageSize,
    total,
    hasMore: params.offset + items.length < total,
  };
}
